import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  AppBar,
  Box,
  Button,
  Card,
  Chip,
  Container,
  Grid,
  InputAdornment,
  Link,
  Popover,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded';
import CloudDoneRounded from '@mui/icons-material/CloudDoneRounded';
import DnsRounded from '@mui/icons-material/DnsRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import GroupsRounded from '@mui/icons-material/GroupsRounded';
import LoginRounded from '@mui/icons-material/LoginRounded';
import PersonSearchRounded from '@mui/icons-material/PersonSearchRounded';
import StorefrontRounded from '@mui/icons-material/StorefrontRounded';
import { Link as RouterLink } from 'react-router';
import { BrandLogo } from '@/components/brand/BrandLogo';
import { ColorModeToggle } from '@/components/ColorModeToggle';
import { DEFAULT_BRAND, type BrandState } from '@/marketplace/brands';
import { BrandLab } from '@/marketplace/components/BrandLab';
import { DuoDemo, GooglePreview, MealAiDemo } from '@/marketplace/components/FeatureDemos';
import { useTenant } from '@/tenant/useTenant';
import { DomainLab } from './components/DomainLab';
import { DriveDemo } from './components/DriveDemo';
import { HeroFan } from './components/HeroFan';
import { LeadForm } from './components/LeadForm';
import { OwnAppShowcase } from './components/OwnAppShowcase';
import { PlanCards } from './components/PlanCards';
import { ReferralAndPlus } from './components/ReferralAndPlus';
import { Reveal } from './components/Reveal';
import { toLabel } from './domain';
import { LandingFooter } from './LandingFooter';

function Section({
  id,
  eyebrow,
  title,
  subtitle,
  children,
  tinted,
}: {
  id: string;
  eyebrow: string;
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
  tinted?: boolean;
}) {
  return (
    <Box
      component="section"
      id={id}
      aria-labelledby={`${id}-title`}
      sx={(theme) => ({
        py: { xs: 7, md: 9 },
        scrollMarginTop: 64,
        ...(tinted && {
          background: `linear-gradient(180deg, ${theme.vars.palette.background.default}, ${theme.alpha(theme.vars.palette.primary.container, 0.45)} 40%, ${theme.vars.palette.background.default})`,
        }),
      })}
    >
      <Container maxWidth="lg">
        <Reveal>
          <Stack
            spacing={1.5}
            sx={{ textAlign: 'center', alignItems: 'center', mb: { xs: 4, md: 5 } }}
          >
            <Chip variant="soft" color="primary" label={eyebrow} />
            <Typography
              id={`${id}-title`}
              variant="h2"
              component="h2"
              sx={{
                maxWidth: 760,
                fontSize: { xs: '1.85rem', md: '2.35rem' },
                textWrap: 'balance',
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography
                variant="h6"
                component="p"
                color="text.secondary"
                sx={{ maxWidth: 680, fontWeight: 400, textWrap: 'balance' }}
              >
                {subtitle}
              </Typography>
            )}
          </Stack>
        </Reveal>
        {children}
      </Container>
    </Box>
  );
}

function Tile({
  icon,
  title,
  text,
  children,
}: {
  icon: ReactNode;
  title: string;
  text: string;
  children: ReactNode;
}) {
  return (
    <Card sx={{ p: { xs: 2.5, md: 3 }, height: '100%' }}>
      <Stack spacing={2} sx={{ height: '100%' }}>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
          <Box
            aria-hidden
            sx={(theme) => ({
              width: 40,
              height: 40,
              flexShrink: 0,
              borderRadius: 2,
              display: 'grid',
              placeItems: 'center',
              bgcolor: theme.vars.palette.primary.container,
              color: theme.vars.palette.primary.onContainer,
            })}
          >
            {icon}
          </Box>
          <Typography variant="h6" component="h3" sx={{ lineHeight: 1.3 }}>
            {title}
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {text}
        </Typography>
        <Box sx={{ mt: 'auto', pt: 1 }}>{children}</Box>
      </Stack>
    </Card>
  );
}

/** "I already have a space": type the address, go there. */
function CustomerLogin() {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [slug, setSlug] = useState('');
  const base = window.location.hostname;
  const go = () => {
    const value = slug.trim().toLowerCase();
    if (!value) return;
    const host = value.includes('.') ? value : `${value}.${base}`;
    const port = window.location.port ? `:${window.location.port}` : '';
    window.location.href = `${window.location.protocol}//${host}${port}/login`;
  };
  return (
    <>
      <Button
        startIcon={<LoginRounded />}
        color="inherit"
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{ whiteSpace: 'nowrap' }}
      >
        Já sou cliente
      </Button>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box
          component="form"
          sx={{ p: 2, width: 340, maxWidth: 'calc(100vw - 32px)' }}
          onSubmit={(e) => {
            e.preventDefault();
            go();
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Qual é o endereço do seu espaço?
          </Typography>
          <TextField
            autoFocus
            size="small"
            fullWidth
            placeholder="suamarca"
            value={slug}
            onChange={(e) => setSlug(e.target.value.replace(/\s/g, ''))}
            slotProps={{
              input: { endAdornment: <InputAdornment position="end">.{base}</InputAdornment> },
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Tem domínio próprio? Digite ele inteiro, como app.suamarca.com.br.
          </Typography>
          <Button type="submit" variant="contained" fullWidth sx={{ mt: 1.5 }}>
            Ir para o meu espaço
          </Button>
        </Box>
      </Popover>
    </>
  );
}

export function LandingPage() {
  const { config } = useTenant();
  const platform = config.platformName;
  const marketplace = config.marketplaceUrl ?? null;
  const base = window.location.hostname;
  const [brand, setBrand] = useState<BrandState>(DEFAULT_BRAND);
  const [domain, setDomain] = useState<string | null>(null);
  const [formKey, setFormKey] = useState(0);
  const formRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.title = `${platform}: app para personal trainer e nutricionista com a sua marca`;
  }, [platform]);

  const toForm = () => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const faq = [
    {
      q: 'O que é a plataforma com a minha marca?',
      a: 'Um app para os seus alunos com o seu nome, suas cores e o seu endereço: treinos com cronômetro e registro de cargas, diário alimentar com IA, medidas e evolução. Você monta os treinos e acompanha tudo num painel.',
    },
    {
      q: 'O que muda de um plano para o outro?',
      a: 'Só o número de alunos. Todos os planos têm treinos, nutrição e evolução, com a sua marca. Depois de entrar, você ativa outros módulos com um clique: finanças é grátis para o profissional, e a IA para fotos de refeição e documentos é um extra de R$ 19,90 por mês para o espaço todo.',
    },
    {
      q: 'Onde ficam as fotos e os documentos dos alunos?',
      a: 'No Google Drive de cada aluno, numa pasta do app. O app só enxerga os arquivos que ele mesmo criou; o resto do Drive continua privado, e o aluno pode apagar tudo quando quiser.',
    },
    {
      q: 'Como funciona o domínio próprio?',
      a: 'Você escolhe o nome (por exemplo, anapersonal.app), a gente registra no seu nome, configura o endereço e o cadeado de segurança (HTTPS) e cuida da renovação. É um serviço cobrado à parte, além do plano.',
    },
    {
      q: 'E se eu não quiser domínio próprio?',
      a: `Sem problema: o plano já inclui um endereço como suamarca.${base}, pronto para usar.`,
    },
    {
      q: 'O perfil no marketplace é pago?',
      a: 'Não. Aparecer no marketplace e receber pedidos de alunos é grátis, com ou sem a plataforma.',
    },
    {
      q: 'O aluno paga alguma coisa?',
      a: 'Não. O aluno entra de graça no espaço do profissional. Se quiser, assina o Plus, com finanças pessoais e fotos de refeição analisadas por IA, que valem em todos os espaços dele.',
    },
    {
      q: 'Como funciona o Indique e ganhe?',
      a: 'Cada pessoa tem um link e um cupom. Quem entra pelo link, pelo convite de um profissional ou digita o cupom antes de pagar fica ligado a quem indicou. No primeiro pagamento dessa pessoa, quem indicou recebe R$ 10 por Pix e ela ganha R$ 5 de desconto na fatura. Não há limite de indicações.',
    },
    {
      q: 'Nutricionista também pode usar?',
      a: 'Pode. E personal e nutricionista podem atender o mesmo aluno, cada um na sua parte, com o aluno decidindo o que cada um vê.',
    },
  ];

  return (
    <Box sx={{ minHeight: '100dvh' }}>
      <AppBar position="sticky">
        <Toolbar sx={{ gap: 1, minHeight: { xs: 60, sm: 68 } }}>
          <Box sx={{ mr: 'auto', minWidth: 0 }}>
            <BrandLogo size="small" />
          </Box>
          <Stack direction="row" spacing={0.5} sx={{ display: { xs: 'none', lg: 'flex' } }}>
            <Button color="inherit" href="#marca">
              Sua marca
            </Button>
            <Button color="inherit" href="#dominio">
              Domínio próprio
            </Button>
            <Button color="inherit" href="#planos">
              Planos
            </Button>
            <Button color="inherit" component={RouterLink} to="/guias">
              Guias
            </Button>
            {marketplace && (
              <Button color="inherit" href={marketplace}>
                Encontrar personal
              </Button>
            )}
          </Stack>
          <ColorModeToggle />
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            <CustomerLogin />
          </Box>
          <Button variant="contained" onClick={toForm} sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              Quero conhecer
            </Box>
            <Box component="span" sx={{ display: { sm: 'none' } }}>
              Conhecer
            </Box>
          </Button>
        </Toolbar>
      </AppBar>

      {/* Hero */}
      <Box
        component="section"
        sx={(theme) => ({
          pt: { xs: 5, md: 8 },
          pb: { xs: 6, md: 9 },
          overflow: 'hidden',
          background: [
            `radial-gradient(60vmax 40vmax at 0% 0%, ${theme.alpha(theme.vars.palette.accent.main, 0.2)}, transparent 60%)`,
            `radial-gradient(60vmax 50vmax at 100% 20%, ${theme.alpha(theme.vars.palette.primary.light, 0.22)}, transparent 60%)`,
            theme.vars.palette.background.default,
          ].join(', '),
        })}
      >
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 4, md: 6 }} sx={{ alignItems: 'center' }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Reveal>
                <Stack spacing={3}>
                  <Chip
                    variant="soft"
                    color="primary"
                    label="Para personal trainers e nutricionistas"
                    sx={{ alignSelf: 'flex-start' }}
                  />
                  <Typography
                    variant="h1"
                    sx={{
                      fontSize: { xs: '2.4rem', sm: '3rem', md: '3.4rem' },
                      textWrap: 'balance',
                    }}
                  >
                    O app do seu treino,{' '}
                    <Box
                      component="span"
                      sx={(theme) => ({
                        background: `linear-gradient(100deg, ${theme.vars.palette.primary.main}, ${theme.vars.palette.accent.dark})`,
                        WebkitBackgroundClip: 'text',
                        backgroundClip: 'text',
                        color: 'transparent',
                      })}
                    >
                      com a sua marca
                    </Box>
                  </Typography>
                  <Typography
                    variant="h6"
                    component="p"
                    color="text.secondary"
                    sx={{ fontWeight: 400, maxWidth: 560 }}
                  >
                    Seus alunos recebem treinos, dieta e evolução num app com o seu nome, suas cores
                    e o seu endereço. E você ainda aparece no marketplace para receber alunos novos.
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <Button
                      size="large"
                      variant="contained"
                      endIcon={<ArrowForwardRounded />}
                      onClick={toForm}
                    >
                      Quero conhecer
                    </Button>
                    <Button size="large" variant="outlined" href="#marca">
                      Ver com a minha marca
                    </Button>
                  </Stack>
                  {marketplace && (
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ alignItems: 'center', color: 'text.secondary' }}
                    >
                      <PersonSearchRounded fontSize="small" color="primary" />
                      <Typography variant="body2">
                        Procurando um personal?{' '}
                        <Link href={marketplace} sx={{ fontWeight: 700 }}>
                          Busque no marketplace →
                        </Link>
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </Reveal>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Reveal delay={150}>
                <HeroFan />
              </Reveal>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Section
        id="marca"
        eyebrow="Sua marca"
        title="Digite o seu nome e escolha a sua cor"
        subtitle="O app dos seus alunos muda na hora, com o contraste ajustado sozinho."
        tinted
      >
        <Reveal>
          <Card sx={{ p: { xs: 2.5, sm: 4, md: 5 } }}>
            <BrandLab value={brand} onChange={setBrand} />
          </Card>
        </Reveal>
      </Section>

      <Section
        id="dominio"
        eyebrow="Domínio próprio"
        title="Seu endereço, sem o nosso nome"
        subtitle={
          <>
            Use o endereço incluso (
            <b>
              {toLabel(brand.name) || 'suamarca'}.{base}
            </b>
            ) ou tenha um domínio só seu, como <b>anapersonal.app</b>. A gente registra, configura e
            renova para você.
          </>
        }
      >
        <Reveal>
          <Card sx={{ p: { xs: 2.5, sm: 4, md: 5 } }}>
            <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', mb: 3 }}>
              <DnsRounded color="primary" />
              <Typography variant="h5" component="h3">
                Veja se o seu domínio está livre
              </Typography>
            </Stack>
            <DomainLab
              brand={brand}
              onWant={(chosen) => {
                setDomain(chosen);
                setFormKey((n) => n + 1);
                toForm();
              }}
            />
          </Card>
        </Reveal>
      </Section>

      <Section
        id="app-proprio"
        eyebrow="App com a sua marca"
        title="O seu app, com o seu nome na loja"
        subtitle="Mude o nome e a cor lá em cima e veja o seu app sendo instalado."
      >
        <Reveal>
          <OwnAppShowcase brand={brand} />
        </Reveal>
      </Section>

      <Section
        id="recursos"
        eyebrow="O que vem junto"
        title="Tudo o que o aluno usa, num lugar só"
        tinted
      >
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Reveal fill>
              <Tile
                icon={<GroupsRounded />}
                title="Personal e nutricionista no mesmo aluno"
                text="Cada um cuida da sua parte e o aluno decide o que cada um pode ver."
              >
                <DuoDemo />
              </Tile>
            </Reveal>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Reveal delay={100} fill>
              <Tile
                icon={<AutoAwesomeRounded />}
                title="O aluno fotografa o prato, a IA anota"
                text="Alimentos e calorias estimados pela foto; o aluno confere e salva. Um extra que você ativa com um clique."
              >
                <MealAiDemo />
              </Tile>
            </Reveal>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Reveal fill>
              <Tile
                icon={<CloudDoneRounded />}
                title="Os arquivos ficam no Google Drive do aluno"
                text="Fotos de refeições e documentos vão para o Drive do próprio aluno, não para um servidor nosso. Os dados são dele, de verdade."
              >
                <DriveDemo folder={platform} />
              </Tile>
            </Reveal>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Reveal delay={100} fill>
              <Tile
                icon={<StorefrontRounded />}
                title="Alunos novos pelo marketplace"
                text="Seu perfil grátis aparece nas buscas da sua cidade e no Google, com o selo de CREF verificado."
              >
                <GooglePreview
                  name={brand.name}
                  color={brand.color}
                  host={marketplace ? new URL(marketplace).hostname : base}
                />
              </Tile>
            </Reveal>
          </Grid>
        </Grid>
      </Section>

      <Section
        id="planos"
        eyebrow="Planos"
        title="Comece grátis. Cresça quando quiser."
        subtitle="Até 5 alunos sem pagar nada. Depois, o plano acompanha o número de alunos, com 14 dias para testar antes da primeira fatura. Finanças e IA você ativa quando quiser."
      >
        <Reveal>
          <PlanCards onInterest={toForm} marketplace={marketplace} />
        </Reveal>
      </Section>

      <Section
        id="indique"
        eyebrow="Indique e ganhe"
        title="Traga gente e ganhe no Pix"
        subtitle="R$ 10 para cada pessoa que você trouxer e que começar a pagar: um aluno que assina o Plus ou um profissional que assina um plano."
        tinted
      >
        <Reveal>
          <ReferralAndPlus />
        </Reveal>
      </Section>

      <Box
        ref={formRef}
        component="section"
        id="quero-conhecer"
        sx={{ py: { xs: 7, md: 9 }, scrollMarginTop: 72 }}
      >
        <Container maxWidth="md">
          <Reveal>
            <Stack spacing={1} sx={{ textAlign: 'center', mb: 4 }}>
              <Typography
                variant="h3"
                component="h2"
                sx={{ fontSize: { xs: '1.85rem', md: '2.35rem' } }}
              >
                Quero conhecer
              </Typography>
              <Typography color="text.secondary">
                Deixe seu contato: mostramos a plataforma com a sua marca e tiramos suas dúvidas
                pelo WhatsApp.
              </Typography>
            </Stack>
            <LeadForm key={formKey} brand={brand} domain={domain} />
          </Reveal>
        </Container>
      </Box>

      <Container
        component="section"
        maxWidth="md"
        sx={{ pb: { xs: 7, md: 9 } }}
        aria-labelledby="faq-title"
      >
        <Typography id="faq-title" variant="h4" component="h2" sx={{ textAlign: 'center', mb: 3 }}>
          Perguntas frequentes
        </Typography>
        {faq.map((item) => (
          <Accordion
            key={item.q}
            disableGutters
            variant="outlined"
            sx={{ mb: 1, '&::before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreRounded />}>
              <Typography variant="subtitle1">{item.q}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color="text.secondary">{item.a}</Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Container>

      <LandingFooter />
    </Box>
  );
}
